import { safeWeightUnitMap } from '~/constants/weight';
import { fetchOrdersV2 } from '~/modules/orders/services/ordersV2.service';
import { type OrderV2 } from '~/modules/orders/types/ordersV2.type';
import { updateOrdersV2 } from '~/modules/orders/utils/ordersV2.util';
import { type LineItemResponse } from '~/services/line-item.service';
import { FeatureFlags, decideFeatureFlag } from '~/utils/featureflags';

export const isOrdersV2Enabled = () => {
  const ordersV2TableFlag = decideFeatureFlag(FeatureFlags.ORDERS_TABLE_API_V2_UI);
  return ordersV2TableFlag?.enabled || false;
};

/**
 * Synchronizes changes made to orders/batch entries in V1 (Redux) to V2 (React Query).
 *
 * This is required for now, given we're still building the write capabilities in V2 API.
 *
 * This works for for all operations except for line items, which require special handling.
 *
 * Rate refreshes are also handled separately, mostly to avoid race conditions.
 */
export const synchronizeOrdersV2 = async ({ orderObjectIds }: { orderObjectIds: string[] }) => {
  if (!isOrdersV2Enabled()) return;

  const ordersToSync = [...new Set(orderObjectIds)];
  if (ordersToSync.length === 0) return;

  const updatedOrders = await fetchOrdersV2({
    object_id: ordersToSync,
    results: ordersToSync.length,
  });

  updateOrdersV2((orderToUpdate) => {
    const updatedOrder = updatedOrders.results.find(
      (updatedOrder) => updatedOrder.object_id === orderToUpdate.object_id,
    );
    return updatedOrder || orderToUpdate;
  });
};

type SynchronizeLineItemToOrdersV2Params = { orderObjectId: string } & (
  | {
      action: 'create' | 'edit';
      lineItem: LineItemResponse;
    }
  | { action: 'delete'; lineItemObjectId: string }
);

const mapLineItemResponseToOrderV2LineItem = (
  lineItem: LineItemResponse,
): OrderV2['line_items'][number] => ({
  amount: { currency: lineItem.currency, value: lineItem.unit_amount },
  country_of_origin: lineItem.country_of_origin,
  dimensions: null,
  id: lineItem.id,
  is_fulfilled: lineItem.is_fulfilled,
  object_id: lineItem.object_id,
  quantity: lineItem.quantity,
  sku: lineItem.sku || '',
  title: lineItem.title,
  variant: lineItem.variant_title ? { title: lineItem.variant_title } : null,
  weight: { unit: safeWeightUnitMap[lineItem.weight_unit] || null, value: lineItem.unit_weight },
});

/**
 * Manually synchronize line item changes to Orders V2 cache.
 *
 * This is required because line item changes take up to 10 seconds to reflect in the Orders V2 API.
 */
export const synchronizeLineItemToOrdersV2 = (args: SynchronizeLineItemToOrdersV2Params) => {
  if (!isOrdersV2Enabled()) return;

  const { action, orderObjectId } = args;

  if (action === 'create') {
    const { lineItem } = args;

    updateOrdersV2((order) => {
      if (order.object_id !== orderObjectId) return order;
      return {
        ...order,
        line_items: [...order.line_items, mapLineItemResponseToOrderV2LineItem(lineItem)],
        order_shipments: order.order_shipments.map((orderShipment) => ({
          ...orderShipment,
          line_items: [...orderShipment.line_items, mapLineItemResponseToOrderV2LineItem(lineItem)],
        })),
      };
    });
  }

  if (action === 'edit') {
    const { lineItem } = args;

    updateOrdersV2((order) => {
      if (order.object_id !== orderObjectId) return order;
      return {
        ...order,
        line_items: order.line_items.map((li) => {
          if (li.object_id !== lineItem.object_id) return li;
          return mapLineItemResponseToOrderV2LineItem(lineItem);
        }),
        order_shipments: order.order_shipments.map((orderShipment) => ({
          ...orderShipment,
          line_items: orderShipment.line_items.map((li) => {
            if (li.object_id !== lineItem.object_id) return li;
            return mapLineItemResponseToOrderV2LineItem(lineItem);
          }),
        })),
      };
    });
  }

  if (action === 'delete') {
    const { lineItemObjectId } = args;

    updateOrdersV2((order) => {
      if (order.object_id !== orderObjectId) return order;
      return {
        ...order,
        line_items: order.line_items.filter((li) => li.object_id !== lineItemObjectId),
        order_shipments: order.order_shipments.map((orderShipment) => ({
          ...orderShipment,
          line_items: orderShipment.line_items.filter((li) => li.object_id !== lineItemObjectId),
        })),
      };
    });
  }
};

/**
 * Manually synchronize rate refresh requests to Orders V2 cache.
 *
 * This sets the rate column to a loading state, and is done manually to avoid race conditions
 * with other updates (while also avoiding an extra API call).
 */
export const synchronizeRateRefreshToOrdersV2 = ({
  orderShipmentIds,
}: {
  orderShipmentIds: number[];
}) => {
  if (!isOrdersV2Enabled()) return;

  updateOrdersV2((order) => ({
    ...order,
    order_shipments: order.order_shipments.map((orderShipment) => {
      if (!orderShipmentIds.includes(orderShipment.id)) return orderShipment;

      return {
        ...orderShipment,
        shipment_object_id: null,
        status: {
          ...orderShipment.status,
          lifecycle_status: 'PROCESSING_SHIPMENT',
        },
      };
    }),
  }));
};
